<template>
  <div v-if="CAN_VIEW">
    <v-container fluid class="pa-6">
      <v-row>
        <v-col cols="12">
          <h2 class="font-weight-bolder">Inventory Audit</h2>
        </v-col>
      </v-row>

      <v-row class="mb-3 d-none d-md-flex">
        <v-col cols="12" class="d-flex align-center justify-end">
          <div class="mr-3">
            <v-autocomplete
              v-model="category_id"
              :items="categories"
              label="Select Category"
              dense
              item-text="name"
              item-value="id"
              flat
              filled
              rounded
              clearable
              min-width="290px"
              class="mt-5 mr-2"
              @change="fetchData"
              color="orange"
            ></v-autocomplete>
          </div>
          <div class="mr-3">
            <v-menu
              ref="openingDateMenu"
              v-model="openingDateMenu"
              :close-on-content-click="false"
              transition="slide-y-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  readonly
                  v-bind="attrs"
                  class="mr-2"
                  depressed
                  rounded
                  x-large
                  v-on="on"
                >
                  <b class="mr-1">Opening:</b>
                  {{ formatDate(openingDate) }}
                  <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-date-picker
                v-model="openingDate"
                no-title
                scrollable
                :max="closingDate"
                @change="onOpeningDateChange"
                color="orange"
              >
                <v-spacer></v-spacer>
              </v-date-picker>
            </v-menu>
          </div>

          <div class="mr-3">
            <v-menu
              ref="closingDateMenu"
              v-model="closingDateMenu"
              :close-on-content-click="false"
              transition="slide-y-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  readonly
                  v-bind="attrs"
                  class="mr-2"
                  depressed
                  rounded
                  x-large
                  v-on="on"
                >
                  <b class="mr-1">Closing:</b>
                  {{ formatDate(closingDate) }}
                  <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-date-picker
                v-model="closingDate"
                no-title
                scrollable
                :min="openingDate"
                @change="onClosingDateChange"
                color="orange"
              >
                <v-spacer></v-spacer>
              </v-date-picker>
            </v-menu>
          </div>

          <v-menu transition="slide-y-transition" bottom>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" rounded depressed x-large>
                Export
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item :href="PDFExportURL">
                <v-list-item-title> PDF</v-list-item-title>
              </v-list-item>
              <v-list-item :href="ExcelExportURL">
                <v-list-item-title> Excel</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>

      <v-row class="mb-6">
        <v-col
          cols="12"
          :class="$vuetify.theme.isDark ? 'grey darken-4' : 'bg-light'"
        >
          <v-skeleton-loader
            type="card"
            v-if="summaryLoading"
          ></v-skeleton-loader>
          <v-row
            v-else
            style="border-radius: 15px"
            :class="
              $vuetify.theme.isDark
                ? 'dark-orange pa-2'
                : 'orange lighten-5 pa-3'
            "
          >
            <v-col cols="12" sm="3">
              <v-card flat class="text-center pa-5">
                <h2 class="font-weight-light mb-0">
                  {{ summaryData.opening_quantity | toMoney }}
                </h2>
                <p class="font-weight-light">Opening Quantity</p>
              </v-card>
            </v-col>
            <v-col cols="12" sm="3">
              <v-card flat class="text-center pa-5">
                <h2 class="font-weight-light mb-0">
                  {{ summaryData.closing_quantity | toMoney }}
                </h2>
                <p class="font-weight-light">Closing Quantity</p>
              </v-card>
            </v-col>
            <v-col cols="12" sm="3">
              <v-card flat class="text-center pa-5">
                <h2 class="font-weight-light mb-0">
                  {{ summaryData.current_quantity | toMoney }}
                </h2>
                <p class="font-weight-light">Current Quantity</p>
              </v-card>
            </v-col>
            <v-col cols="12" sm="3">
              <v-card flat class="text-center pa-5">
                <v-card-text class="text-center">
                  <v-btn
                    @click="showDetailsDialog = true"
                    text
                    rounded
                    color="orange"
                  >
                    More summaries...
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-skeleton-loader
        type="table"
        v-if="inventoryLoading"
      ></v-skeleton-loader>
      <div v-else>
        <v-data-table
          :headers="headers"
          :items="inventory.data"
          item-key="id"
          hide-default-footer
          disable-pagination
          fixed-header
          height="90vh"
        >
          <template v-slot:header.quantity_in="{ header }">
            <div
              class="text-right in-coloumn orange--text font-weight-black pa-4"
            >
              {{ header.text }}
              <v-tooltip top color="black" max-width="400">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" style="cursor: pointer" small class="ml-1">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>
                  <strong>What are quantities in?</strong><br />
                  These are quantities that you record in transactions such as
                  bills and quantity increment stock adjustments.<br />
                  These transactions turn to increase the quantity of the item.
                </span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:header.quantity_out="{ header }">
            <div
              class="text-right out-column green--text font-weight-black pa-4"
            >
              {{ header.text }}
              <v-tooltip top color="black" max-width="400">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" style="cursor: pointer" small class="ml-1">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>
                  <strong>What are quantities out?</strong><br />
                  These are quantities that you record in transactions such as
                  Quick sales, Invoices and quantity decrement stock
                  adjustments.<br />
                  These transactions turn to reduce the quantity of the item.
                </span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:header.opening_quantity="{ header }">
            <div class="text-right bg-light font-weight-black">
              {{ header.text }}
            </div>
          </template>

          <template v-slot:header.current_quantity="{ header }">
            <div class="text-right font-weight-black">
              {{ header.text }}
            </div>
          </template>

          <template v-slot:item.quantity="{ item }">
            <div
              class="text-right"
              :class="{ 'white--text': $vuetify.theme.isDark }"
            >
              {{ item.quantity }}
            </div>
          </template>

          <template v-slot:item.opening_quantity="{ item }">
            <div
              class="text-right font-weight-black"
              :class="{
                'black--text': !$vuetify.theme.isDark,
                'white--text': $vuetify.theme.isDark,
              }"
            >
              {{ item.opening_quantity }}
            </div>
          </template>

          <template v-slot:item.quantity_in="{ item }">
            <div class="text-right in-coloumn pa-8 orange--text">
              {{ item.quantity_in }}
              <v-btn
                text
                x-small
                rounded
                color="amber darken-2"
                @click="handleQuantityInClick(item.id)"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small v-bind="attrs" v-on="on">mdi-anchor</v-icon>
                  </template>
                  <span>View associated transactions</span>
                </v-tooltip>
              </v-btn>
            </div>
          </template>

          <template v-slot:item.quantity_out="{ item }">
            <div class="text-right pa-8 out-column green--text">
              {{ item.quantity_out }}
              <v-btn
                text
                x-small
                rounded
                color="green darken-2"
                @click="handleQuantityOutClick(item.id)"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small v-bind="attrs" v-on="on">mdi-anchor</v-icon>
                  </template>
                  <span>View associated transactions</span>
                </v-tooltip>
              </v-btn>
            </div>
          </template>
          <template v-slot:item.closing_quantity="{ item }">
            <div
              class="text-right font-weight-black"
              :class="{
                'black--text': !$vuetify.theme.isDark,
                'white--text': $vuetify.theme.isDark,
              }"
            >
              {{ item.closing_quantity }}
            </div>
          </template>

          <template v-slot:item.name="{ item }">
            <v-list-item class="pa-0">
              <v-list-item-avatar rounded="lg" size="60">
                <v-img :src="item.photo"></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        class="text-none pa-0 pb-0 pt-0"
                        v-on="on"
                        :to="'/inventory/view/' + item.id"
                        text
                        target="_blank"
                        :color="$vuetify.theme.isDark ? 'white' : 'orange'"
                        >{{ item.name }}
                        <sup>
                          <v-icon x-small>mdi-open-in-new</v-icon>
                        </sup>
                        <sup v-if="item.archived">
                          <v-icon color="red" x-small
                            >mdi-archive-outline
                          </v-icon>
                        </sup>
                      </v-btn>
                    </template>
                    <span>View Details</span>
                  </v-tooltip>
                </v-list-item-title>
                <v-list-item-subtitle>
                  <small>{{ item.description }}</small>
                  <span class="d-block text--disabled"
                    >Code:{{ item.code }}</span
                  >
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-data-table>

        <v-container>
          <v-row justify="center">
            <pagination-component
              :links="inventory.links"
              @goto="go_Topage"
            ></pagination-component>
          </v-row>
        </v-container>
      </div>

      <v-dialog
        transition="dialog-bottom-transition"
        scrollable
        width="500"
        v-model="showDetailsDialog"
      >
        <v-card>
          <v-card-title> Inventory summary details</v-card-title>
          <v-card-text>
            <v-row
              dense
              class="rounded-lg"
              :class="
                $vuetify.theme.isDark
                  ? 'dark-orange pa-2'
                  : 'orange lighten-5 pa-3'
              "
            >
              <v-col cols="12" sm="6">
                <v-card flat height="100px">
                  <v-card-text class="text-center">
                    <strong class="d-block font-weight-black text-h5">{{
                      summaryData.quantity_in | toMoney
                    }}</strong>
                    <small>Total Quantity In </small>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card flat height="100px">
                  <v-card-text class="text-center">
                    <strong class="d-block font-weight-black text-h5">{{
                      summaryData.quantity_out | toMoney
                    }}</strong>
                    <small>Total Quantity Out </small>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card flat height="100px">
                  <v-card-text class="text-center">
                    <strong class="d-block font-weight-black text-h5">{{
                      summaryData.invoices | toMoney
                    }}</strong>
                    <small>Invoices</small>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card flat height="100px">
                  <v-card-text class="text-center">
                    <strong class="d-block font-weight-black text-h5">{{
                      summaryData.bills | toMoney
                    }}</strong>
                    <small>Bills</small>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card flat height="100px">
                  <v-card-text class="text-center">
                    <strong class="d-block font-weight-black text-h5">{{
                      summaryData.purchases | toMoney
                    }}</strong>
                    <small>Purchases</small>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card flat height="100px">
                  <v-card-text class="text-center">
                    <strong class="d-block font-weight-black text-h5">{{
                      summaryData.sales | toMoney
                    }}</strong>
                    <small>Sales</small>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card flat height="100px">
                  <v-card-text class="text-center">
                    <strong class="d-block font-weight-black text-h5">{{
                      summaryData.write_offs | toMoney
                    }}</strong>
                    <small>Write Offs</small>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="dialogOpen"
        persistent
        scrollable
        fullscreen
        transition="dialog-bottom-transition"
      >
        <v-card>
          <v-card-title class="font-weight-light">
            {{ dialogTitle }}
            <v-spacer></v-spacer>
            <v-btn
              color="red lighten-5 red--text"
              depressed
              rounded
              small
              @click="dialogOpen = false"
            >
              close
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-subtitle>
            Details of transactions contributing to the selected quantity.
          </v-card-subtitle>
          <v-card-text>
            <v-skeleton-loader
              type="table"
              v-if="dialogLoading"
            ></v-skeleton-loader>
            <div v-else>
              <v-tabs
                v-model="activeTab"
                centered
                fixed-tabs
                style="position: sticky; top: 0; z-index: 1; background: white"
                color="orange"
              >
                <v-tab v-for="tab in availableTabs" :key="tab.key">
                  {{ tab.label }}
                  <v-chip small class="ml-2" color="orange white--text">
                    {{ tab.data.length }}
                  </v-chip>
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="activeTab">
                <v-tab-item v-for="tab in availableTabs" :key="tab.key">
                  <div class="pt-4">
                    <div v-if="!tab.data || tab.data.length === 0">
                      <div class="text-center text--secondary pa-4">
                        No {{ tab.label.toLowerCase() }} found for this period.
                      </div>
                    </div>
                    <div v-else>
                      <div v-if="tab.key === 'stockAdjustments'">
                        <AssociatedStockAdjustments :items="tab.data" />
                      </div>
                      <div v-else-if="tab.key === 'sales'">
                        <AssociatedSales
                          :items="tab.data"
                          :hasQuantity="true"
                        />
                      </div>
                    </div>
                  </div>
                </v-tab-item>
              </v-tabs-items>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
  <cannot-view-component v-else></cannot-view-component>
</template>

<script>
import PaginationComponent from "../../components/PaginationComponent.vue";
import AssociatedSales from "../../components/batches/AssociatedSales.vue";
import AssociatedStockAdjustments from "../../components/batches/AssociatedStockAdjustments.vue";
import CannotViewComponent from "../../components/CannotViewComponent.vue";
import moment from "moment";

export default {
  name: "InventoryDashboard",
  components: {
    PaginationComponent,
    AssociatedSales,
    AssociatedStockAdjustments,
    CannotViewComponent,
  },
  data() {
    return {
      asAtDate: new Date().toISOString().substr(0, 10),
      dateMenu: false,
      dateMenuMobile: false,
      dialogOpen: false,
      dialogTitle: "",
      dialogTransactions: [],
      summaryLoading: true,
      category_id: null,
      categories: [],
      inventoryLoading: true,
      dialogLoading: false,
      showDetailsDialog: false,
      openingDate: moment().startOf("month").format("YYYY-MM-DD"),
      closingDate: moment().format("YYYY-MM-DD"),
      openingDateMenu: false,
      closingDateMenu: false,
      inventory: { data: [], links: [] },
      summaryData: {
        current_quantity: 0,
        opening_quantity: 0,
        quantity_in: 0,
        bills: 0,
        purchases: 0,
        quantity_out: 0,
        sales: 0,
        invoices: 0,
        write_offs: 0,
        closing_quantity: 0,
      },
      headers: [
        { text: "Item Name", value: "name", sortable: true },
        {
          text: "Current Quantity",
          value: "quantity",
          sortable: true,
          align: "right",
        },
        {
          text: "Opening Quantity",
          value: "opening_quantity",
          sortable: true,
          align: "right",
        },
        {
          text: "Quantity In (as at date)",
          value: "quantity_in",
          sortable: false,
          align: "right",
        },
        {
          text: "Quantity Out (as at date)",
          value: "quantity_out",
          sortable: false,
          align: "right",
        },
        {
          text: "Closing Quantity",
          value: "closing_quantity",
          sortable: true,
          align: "right",
        },
      ],
      activeTab: 0,
      dialogTransactionType: "",
      dialogTransactionData: {
        adjustments: [],
        bills: [],
        invoices: [],
        sales: [],
      },
    };
  },

  computed: {
    CAN_VIEW() {
      if (this.$store.state.user.is_admin === 1) {
        return true;
      } else return this.$store.state.user.components[15].roles.view === 1;
    },
    CAN_DOWNLOAD() {
      if (this.$store.state.user.is_admin === 1) {
        return true;
      } else return this.$store.state.user.components[15].roles.download === 1;
    },
    availableTabs() {
      if (this.dialogTransactionType === "in") {
        return [
          {
            key: "stockAdjustments",
            label: "Adjustments",
            data: this.dialogTransactionData.adjustments,
          },
        ];
      } else if (this.dialogTransactionType === "out") {
        return [
          {
            key: "stockAdjustments",
            label: "Adjustments",
            data: this.dialogTransactionData.adjustments,
          },
          {
            key: "sales",
            label: "Sales",
            data: this.dialogTransactionData.sales,
          },
        ];
      }
      return [];
    },
    businessEncId() {
      return this.$store.state.user.current_business.enc_id;
    },
    baseUrl() {
      return this.$store.state.baseUrl;
    },
    currentPage() {
      return this.$route.query.page ?? 1;
    },
    PDFExportURL() {
      let url = `${this.baseUrl}/stock-audit/download-pdf?start_date=${this.openingDate}&end_date=${this.closingDate}&enc_id=${this.businessEncId}`;

      if (this.category_id) {
        url = url + "&category_id=" + this.category_id;
      }
      return url;
    },
    ExcelExportURL() {
      let url = `${this.baseUrl}/stock-audit/download-excel?start_date=${this.openingDate}&end_date=${this.closingDate}&enc_id=${this.businessEncId}`;

      if (this.category_id) {
        url = url + "&category_id=" + this.category_id;
      }
      return url;
    },
  },

  methods: {
    go_Topage(page) {
      this.$router.push("/reports/analytics/inventory-audit?page=" + page);
      this.fetchStockAudit();
    },
    fetchData() {
      Promise.all([this.fetchSummary(), this.fetchStockAudit()]).catch(
        () => {}
      );
    },

    fetchSummary() {
      this.summaryLoading = true;

      const params = {
        start_date: this.openingDate,
        end_date: this.closingDate,
        page: this.currentPage,
      };

      if (this.category_id) {
        params.category_id = this.category_id;
      }
      return window.axios
        .get("/api/stock-audit/summary", {
          params: params,
        })
        .then((response) => {
          this.summaryData = response.data;
        })
        .catch(() => {})
        .finally(() => {
          this.summaryLoading = false;
        });
    },

    fetchStockAudit() {
      this.inventoryLoading = true;

      const params = {
        start_date: this.openingDate,
        end_date: this.closingDate,
        page: this.currentPage,
      };
      if (this.category_id) {
        params.category_id = this.category_id;
      }

      return window.axios
        .get("/api/stock-audit", {
          params: params,
        })
        .then((response) => {
          this.inventory = response.data;
        })
        .catch(() => {})
        .finally(() => {
          this.inventoryLoading = false;
        });
    },
    get_categories() {
      window.axios.get("/api/itemcategories").then((res) => {
        this.categories = res.data;
      });
    },

    onOpeningDateChange(date) {
      this.openingDate = date;
      this.openingDateMenu = false;
      this.fetchData();
    },

    onClosingDateChange(date) {
      this.closingDate = date;
      this.closingDateMenu = false;
      this.fetchData();
    },
    async handleQuantityInClick(itemId) {
      this.dialogLoading = true;
      this.dialogOpen = true;
      this.dialogTransactionType = "in";
      this.activeTab = 0;

      const item = this.inventory.data.find((i) => i.id === itemId);
      this.dialogTitle = `Quantity In Records for ${item?.name}`;

      this.dialogTransactionData = {
        adjustments: [],
        bills: [],
        invoices: [],
        sales: [],
      };

      try {
        const response = await window.axios.get(`/api/stock-audit/${itemId}/ins`, {
          params: {
            start_date: this.openingDate,
            end_date: this.closingDate,
          },
        });

        const data = response.data || {};

        this.dialogTransactionData.adjustments = data.adjustments;
        this.dialogTransactionData.bills = data.bills.map((item) => ({
          ...item.bill,
          quantity: item.quantity,
        }));
      } catch (error) {
        console.log(error);
      } finally {
        this.dialogLoading = false;
      }
    },

    async handleQuantityOutClick(itemId) {
      this.dialogLoading = true;
      this.dialogOpen = true;
      this.dialogTransactionType = "out";
      this.activeTab = 0;

      const item = this.inventory.data.find((i) => i.id === itemId);
      this.dialogTitle = `Quantity Out Records for ${item?.name}`;

      this.dialogTransactionData = {
        adjustments: [],
        bills: [],
        invoices: [],
        sales: [],
      };

      try {
        const response = await window.axios.get(`/api/stock-audit/${itemId}/outs`, {
          params: {
            start_date: this.openingDate,
            end_date: this.closingDate,
          },
        });

        const data = response.data || [];

        this.dialogTransactionData.adjustments = data.adjustments;
        this.dialogTransactionData.invoices = data.invoices.map((item) => ({
          ...item.invoice,
          quantity: item.invoice_quantity,
        }));
        this.dialogTransactionData.sales = data.sales.map((item) => ({
          ...item.sale,
          quantity: item.quantity,
        }));
      } catch (error) {
        console.log(error);
      } finally {
        this.dialogLoading = false;
      }
    },

    handleExportPdf() {
      const url = `${this.baseUrl}/stock-audit/download-pdf?start_date=${this.openingDate}&end_date=${this.closingDate}&enc_id=${this.businessEncId}`;
      window.open(url);
    },

    handleExportExcel() {
      const url = `${this.baseUrl}/stock-audit/download-excel?start_date=${this.openingDate}&end_date=${this.closingDate}&enc_id=${this.businessEncId}`;
      window.open(url, "_blank");
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    getTransactionTypeColor(type) {
      const colors = {
        bill: "blue",
        sale: "green",
        invoice: "purple",
        adjustment: "orange",
        "write-off": "red",
      };
      return colors[type] || "grey";
    },
  },

  mounted() {
    this.fetchData();
    this.get_categories();
  },
};
</script>

<style>
.in-coloumn {
  background-color: rgba(255, 159, 0, 0.2);
}

.out-column {
  background-color: rgba(56, 142, 60, 0.2);
}
</style>
